/**
 * Centralized Environment Configuration
 * Single source of truth for all environment variables
 *
 * Rules enforced:
 * - All env vars validated with Zod
 * - Server-only vars never exposed to client
 * - Type-safe access throughout codebase
 * - Explicit defaults for optional values
 */
import { z } from "zod";

/**
 * Server-side environment schema
 * These values are NEVER exposed to the client
 */
const serverEnvSchema = z.object({
  // Node environment
  NODE_ENV: z.enum(["development", "production", "test"]).default("development"),

  // Database
  DATABASE_URL: z.string().optional(),
  DIRECT_URL: z.string().optional(),
  DATABASE_POOL_SIZE: z
    .string()
    .optional()
    .transform((v) => parseInt(v || "10", 10)),
  USE_DATABASE: z
    .string()
    .optional()
    .transform((v) => v !== "false"),

  // Authentication - JWT (for multi-stakeholder auth)
  JWT_SECRET: z.string().optional(),

  // AI Services - OpenAI
  OPENAI_API_KEY: z.string().optional(),

  // Search - Algolia (admin key for indexing)
  ALGOLIA_ADMIN_KEY: z.string().optional(),

  // Lyfe Data Network Integration
  ZUS_API_URL: z.string().optional(),
  ZUS_ACCESS_TOKEN: z.string().optional(),
  ZUS_CLIENT_ID: z.string().optional(),
  ZUS_CLIENT_SECRET: z.string().optional(),
  ZUS_AUTH_URL: z.string().optional(),
  ZUS_IDENTIFIER_SYSTEM: z.string().optional(),
  ZUS_PACKAGE_ID: z.string().optional(),
  ZUS_PRACTITIONER_NPI: z.string().optional(),
  ZUS_PRACTICE_NAME: z.string().optional(),
  ZUS_PRACTITIONER_ROLE: z.string().default("Primary Care Provider"),
  ZUS_BUILDER_ID: z.string().optional(),
  ZUS_ENVIRONMENT: z.enum(["sandbox", "production"]).optional().default("production"),
  ZUS_WEBHOOK_SECRET: z.string().optional(),

  // External Services
  METRIPORT_API_URL: z.string().optional().default("https://api.metriport.com"),
  ZUS_HEALTH_API_URL: z.string().optional().default("https://api.zushealth.com"),
  FHIR_SERVER_URL: z.string().optional().default("https://fhir.example.com"),

  // DrChrono Integration
  DRCHRONO_CLIENT_ID: z.string().optional(),
  DRCHRONO_CLIENT_SECRET: z.string().optional(),
  DRCHRONO_REDIRECT_URI: z.string().optional(),

  // Medplum (hosted at api.medplum.com — see lib/medplum/shadow-import-service.ts)
  MEDPLUM_BASE_URL: z.string().optional(),
  MEDPLUM_CLIENT_ID: z.string().optional(),
  MEDPLUM_CLIENT_SECRET: z.string().optional(),
  // Default Medplum Organization to stamp on imported resources (multi-tenant filter target).
  // When we onboard more clinics, this becomes a per-org lookup; for now one clinic.
  MEDPLUM_DEFAULT_ORG_ID: z.string().optional(),
  // Feature flag: must be "true" for any shadow import to fire. Off in production by default.
  ENABLE_MEDPLUM_SHADOW_IMPORT: z
    .string()
    .optional()
    .transform((v) => v === "true"),
  // Daily safety cap on shadow imports (prevents runaway calls if upstream loops). Default 50.
  MEDPLUM_SHADOW_IMPORT_DAILY_CAP: z
    .string()
    .optional()
    .transform((v) => parseInt(v || "50", 10)),
  // Feature flag: when "true", the reciprocity push step reads from
  // Medplum and POSTs Conditions/Medications/Allergies/Observations to
  // Zus. Off by default — keep the legacy encounter-only push as the
  // safe default until staging-validated.
  ENABLE_MEDPLUM_RECIPROCITY_PUSH: z
    .string()
    .optional()
    .transform((v) => v === "true"),

  // Svix Webhooks
  SVIX_API_KEY: z.string().optional(),
  SVIX_APP_ID: z.string().default("lyfe-health"),

  // Supabase (server-side service role key)
  SUPABASE_SERVICE_ROLE_KEY: z.string().optional(),

  // Encryption
  ENCRYPTION_KEY: z.string().optional(),

  // Email Service Configuration
  EMAIL_PROVIDER: z.enum(["sendgrid", "ses", "smtp", "mock"]).optional().default("mock"),
  SENDGRID_API_KEY: z.string().optional(),
  EMAIL_API_KEY: z.string().optional(),
  EMAIL_FROM_ADDRESS: z.string().email().optional().default("noreply@lyfeco.ai"),
  EMAIL_FROM_NAME: z.string().optional().default("LyfeAI"),

  // SMS Service Configuration (Twilio)
  SMS_PROVIDER: z.enum(["twilio", "mock"]).optional().default("mock"),
  TWILIO_ACCOUNT_SID: z.string().optional(),
  TWILIO_AUTH_TOKEN: z.string().optional(),
  TWILIO_FROM_NUMBER: z.string().optional().default("+15555555555"),

  // Monitoring
  MONITORING_DEBUG: z
    .string()
    .optional()
    .transform((v) => v === "true"),

  // Multi-tenant Configuration
  ALLOW_LEGACY_PATIENT_ACCESS: z
    .string()
    .optional()
    .transform((v) => v === "true"),

  // SMART on FHIR Configuration
  SMART_CLIENT_ID: z.string().optional(),
  SMART_CLIENT_SECRET: z.string().optional(),
  SMART_CALLBACK_PATH: z.string().optional(),
  SMART_DEFAULT_SCOPES: z.string().optional(),
  SMART_SESSION_TTL_SECONDS: z.string().optional(),
  SMART_STATE_TTL_SECONDS: z.string().optional(),
  SMART_USE_PKCE: z.string().optional(),
  SMART_DEBUG: z.string().optional(),
  SMART_ATHENAHEALTH_CLIENT_ID: z.string().optional(),

  // Vercel Configuration
  VERCEL_URL: z.string().optional(),
  VERCEL_READ_WRITE_TOKEN: z.string().optional(),
});

/**
 * Client-side environment schema
 * These values are safe to expose to the browser
 */
const clientEnvSchema = z.object({
  // Application URL - REQUIRED for OAuth callbacks and redirects
  // Must be set per environment: app.lyfeco.ai (prod), dev.getlyfe.dev (dev), staging.getlyfe.dev (staging)
  NEXT_PUBLIC_APP_URL: z.string().optional(),

  // Algolia (public search key)
  NEXT_PUBLIC_ALGOLIA_APP_ID: z.string().optional(),
  NEXT_PUBLIC_ALGOLIA_SEARCH_KEY: z.string().optional(),

  // Supabase (public)
  NEXT_PUBLIC_SUPABASE_URL: z.string().optional(),
  NEXT_PUBLIC_SUPABASE_ANON_KEY: z.string().optional(),

  // Feature flags
  NEXT_PUBLIC_AUTH_BYPASS: z
    .string()
    .optional()
    .transform((v) => v === "true"),

  // Application Version
  NEXT_PUBLIC_APP_VERSION: z.string().optional().default("0.1.0"),
});

// Parse and validate server environment
function getServerEnv() {
  const parsed = serverEnvSchema.safeParse(process.env);
  if (!parsed.success) {
    console.error("❌ Invalid server environment variables:", parsed.error.flatten().fieldErrors);
    // Don't throw in production - allow graceful degradation
    if (process.env.NODE_ENV === "development") {
      console.warn("⚠️ Some environment variables are missing. Features may be limited.");
    }
    // Return partial data with defaults
    return serverEnvSchema.parse({});
  }
  return parsed.data;
}

// Parse and validate client environment
function getClientEnv() {
  const clientProcessEnv = {
    NEXT_PUBLIC_APP_URL: process.env.NEXT_PUBLIC_APP_URL,
    NEXT_PUBLIC_ALGOLIA_APP_ID: process.env.NEXT_PUBLIC_ALGOLIA_APP_ID,
    NEXT_PUBLIC_ALGOLIA_SEARCH_KEY: process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_KEY,
    NEXT_PUBLIC_SUPABASE_URL: process.env.NEXT_PUBLIC_SUPABASE_URL,
    NEXT_PUBLIC_SUPABASE_ANON_KEY: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY,
    NEXT_PUBLIC_AUTH_BYPASS: process.env.NEXT_PUBLIC_AUTH_BYPASS,
    NEXT_PUBLIC_APP_VERSION: process.env.NEXT_PUBLIC_APP_VERSION,
  };

  const parsed = clientEnvSchema.safeParse(clientProcessEnv);
  if (!parsed.success) {
    console.error("❌ Invalid client environment variables:", parsed.error.flatten().fieldErrors);
    return clientEnvSchema.parse({});
  }
  return parsed.data;
}

// Lazy initialization to avoid issues during build
let _serverEnv: ReturnType<typeof getServerEnv> | null = null;
let _clientEnv: ReturnType<typeof getClientEnv> | null = null;

/**
 * Server-side environment variables
 * Import this only in server components, API routes, and server actions
 */
export const serverEnv = new Proxy({} as ReturnType<typeof getServerEnv>, {
  get(_, prop: string) {
    if (_serverEnv === null) {
      _serverEnv = getServerEnv();
    }
    return _serverEnv[prop as keyof typeof _serverEnv];
  },
});

/**
 * Client-side environment variables
 * Safe to use in any component
 */
export const clientEnv = new Proxy({} as ReturnType<typeof getClientEnv>, {
  get(_, prop: string) {
    if (_clientEnv === null) {
      _clientEnv = getClientEnv();
    }
    return _clientEnv[prop as keyof typeof _clientEnv];
  },
});

/**
 * Type exports for use in other modules
 */
export type ServerEnv = z.infer<typeof serverEnvSchema>;
export type ClientEnv = z.infer<typeof clientEnvSchema>;

/**
 * Feature availability checks
 */
export const features = {
  get hasDatabase() {
    return Boolean(serverEnv.DATABASE_URL);
  },
  get hasOpenAI() {
    return Boolean(serverEnv.OPENAI_API_KEY);
  },
  get hasZus() {
    return Boolean(serverEnv.ZUS_API_URL && serverEnv.ZUS_ACCESS_TOKEN);
  },
  get hasZusCredentials() {
    return Boolean(serverEnv.ZUS_CLIENT_ID && serverEnv.ZUS_CLIENT_SECRET);
  },
  get hasZusWebhook() {
    return Boolean(serverEnv.ZUS_WEBHOOK_SECRET);
  },
  get hasDrChrono() {
    return Boolean(serverEnv.DRCHRONO_CLIENT_ID && serverEnv.DRCHRONO_CLIENT_SECRET);
  },
  get hasSvix() {
    return Boolean(serverEnv.SVIX_API_KEY);
  },
  get hasAlgolia() {
    return Boolean(clientEnv.NEXT_PUBLIC_ALGOLIA_APP_ID && clientEnv.NEXT_PUBLIC_ALGOLIA_SEARCH_KEY);
  },
  get hasJwtSecret() {
    return Boolean(serverEnv.JWT_SECRET);
  },
  get isProduction() {
    return serverEnv.NODE_ENV === "production";
  },
  get isDevelopment() {
    return serverEnv.NODE_ENV === "development";
  },
  get isTest() {
    return serverEnv.NODE_ENV === "test";
  },
  get hasEncryption() {
    return Boolean(serverEnv.ENCRYPTION_KEY);
  },
  get hasEmail() {
    return Boolean(serverEnv.SENDGRID_API_KEY || serverEnv.EMAIL_API_KEY);
  },
  get hasSms() {
    return Boolean(serverEnv.TWILIO_ACCOUNT_SID && serverEnv.TWILIO_AUTH_TOKEN);
  },
  get hasSmart() {
    return Boolean(serverEnv.SMART_CLIENT_ID || serverEnv.SMART_ATHENAHEALTH_CLIENT_ID);
  },
  get hasVercelBlob() {
    return Boolean(serverEnv.VERCEL_READ_WRITE_TOKEN);
  },
  get hasSupabase() {
    return Boolean(clientEnv.NEXT_PUBLIC_SUPABASE_URL && clientEnv.NEXT_PUBLIC_SUPABASE_ANON_KEY);
  },
  get allowLegacyPatientAccess() {
    return serverEnv.ALLOW_LEGACY_PATIENT_ACCESS;
  },
  get useSupabaseAuth() {
    // Supabase Auth is now the default auth provider
    return true;
  },
  get monitoringDebug() {
    return serverEnv.MONITORING_DEBUG;
  },
};

/**
 * External Services Configuration
 * URLs for third-party integrations with validated defaults
 */
export const externalServices = {
  get metriportApi() {
    return serverEnv.METRIPORT_API_URL;
  },
  get zusHealthApi() {
    return serverEnv.ZUS_HEALTH_API_URL;
  },
  get fhirServer() {
    return serverEnv.FHIR_SERVER_URL;
  },
};

/**
 * Database Configuration
 * Centralized database settings
 */
export const databaseConfig = {
  get url() {
    return serverEnv.DATABASE_URL;
  },
  get directUrl() {
    return serverEnv.DIRECT_URL;
  },
  get poolSize() {
    return serverEnv.DATABASE_POOL_SIZE;
  },
};

/**
 * URL Configuration
 * Centralized URL helpers to eliminate hardcoded URLs throughout the codebase
 *
 * Environment-specific URLs:
 * - Production: https://app.lyfeco.ai
 * - Development: https://dev.getlyfe.dev
 * - Staging: https://staging.getlyfe.dev
 * - Local: http://localhost:3000
 */
export const urls = {
  /**
   * Get the base application URL
   * IMPORTANT: NEXT_PUBLIC_APP_URL must be set in each environment
   */
  get app(): string {
    const url = clientEnv.NEXT_PUBLIC_APP_URL;
    if (!url) {
      // Fallback for local development only
      if (typeof window !== "undefined") {
        return window.location.origin;
      }
      console.warn("⚠️ NEXT_PUBLIC_APP_URL not set. Using localhost fallback.");
      return "http://localhost:3000";
    }
    return url.replace(/\/$/, ""); // Remove trailing slash
  },

  /**
   * Patient portal sign-in / magic-link landing page
   * Used as the redirectTo target in Supabase Auth invitations
   */
  get patientPortal(): string {
    return `${this.app}/patient`;
  },

  /**
   * DrChrono OAuth callback URL
   */
  get drchronoCallback(): string {
    return `${this.app}/api/drchrono/callback`;
  },

  /**
   * DrChrono launch callback URL (for EHR iframe launch)
   */
  get drchronoLaunchCallback(): string {
    return `${this.app}/api/drchrono/launch-callback`;
  },

  /**
   * Patient import page with optional params
   */
  patientImport(params?: { error?: string; success?: string; tab?: string }): string {
    const base = `${this.app}/patients/import`;
    if (!params) return base;
    const searchParams = new URLSearchParams();
    if (params.error) searchParams.set("error", params.error);
    if (params.success) searchParams.set("success", params.success);
    if (params.tab) searchParams.set("tab", params.tab);
    const query = searchParams.toString();
    return query ? `${base}?${query}` : base;
  },

  /**
   * API endpoint URL builder
   */
  api(path: string): string {
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return `${this.app}${cleanPath}`;
  },
};

/**
 * Email Service Configuration
 */
export const emailConfig = {
  get provider() {
    return serverEnv.EMAIL_PROVIDER;
  },
  get apiKey() {
    return serverEnv.SENDGRID_API_KEY || serverEnv.EMAIL_API_KEY;
  },
  get fromEmail() {
    return serverEnv.EMAIL_FROM_ADDRESS;
  },
  get fromName() {
    return serverEnv.EMAIL_FROM_NAME;
  },
};

/**
 * SMS Service Configuration
 */
export const smsConfig = {
  get provider() {
    return serverEnv.SMS_PROVIDER;
  },
  get accountSid() {
    return serverEnv.TWILIO_ACCOUNT_SID;
  },
  get authToken() {
    return serverEnv.TWILIO_AUTH_TOKEN;
  },
  get fromNumber() {
    return serverEnv.TWILIO_FROM_NUMBER;
  },
};

/**
 * SMART on FHIR Configuration
 */
export const smartConfig = {
  get clientId() {
    return serverEnv.SMART_CLIENT_ID;
  },
  get clientSecret() {
    return serverEnv.SMART_CLIENT_SECRET;
  },
  get callbackPath() {
    return serverEnv.SMART_CALLBACK_PATH;
  },
  get defaultScopes() {
    return serverEnv.SMART_DEFAULT_SCOPES;
  },
  get sessionTtlSeconds() {
    return serverEnv.SMART_SESSION_TTL_SECONDS;
  },
  get stateTtlSeconds() {
    return serverEnv.SMART_STATE_TTL_SECONDS;
  },
  get usePKCE() {
    return serverEnv.SMART_USE_PKCE;
  },
  get debug() {
    return serverEnv.SMART_DEBUG;
  },
  get athenaHealthClientId() {
    return serverEnv.SMART_ATHENAHEALTH_CLIENT_ID;
  },
};

/**
 * Supabase Configuration
 */
export const supabaseConfig = {
  get url() {
    return clientEnv.NEXT_PUBLIC_SUPABASE_URL;
  },
  get anonKey() {
    return clientEnv.NEXT_PUBLIC_SUPABASE_ANON_KEY;
  },
  get serviceRoleKey() {
    return serverEnv.SUPABASE_SERVICE_ROLE_KEY;
  },
};

/**
 * Lyfe Data Network Configuration
 */
export const zusConfig = {
  get apiUrl() {
    return serverEnv.ZUS_API_URL;
  },
  get accessToken() {
    return serverEnv.ZUS_ACCESS_TOKEN;
  },
  get clientId() {
    return serverEnv.ZUS_CLIENT_ID;
  },
  get clientSecret() {
    return serverEnv.ZUS_CLIENT_SECRET;
  },
  get authUrl() {
    return serverEnv.ZUS_AUTH_URL;
  },
  get identifierSystem() {
    return serverEnv.ZUS_IDENTIFIER_SYSTEM;
  },
  get packageId() {
    return serverEnv.ZUS_PACKAGE_ID;
  },
  get practitionerNpi() {
    return serverEnv.ZUS_PRACTITIONER_NPI;
  },
  get practiceName() {
    return serverEnv.ZUS_PRACTICE_NAME;
  },
  get practitionerRole() {
    return serverEnv.ZUS_PRACTITIONER_ROLE;
  },
  get builderId() {
    return serverEnv.ZUS_BUILDER_ID;
  },
  get environment() {
    return serverEnv.ZUS_ENVIRONMENT;
  },
  get webhookSecret() {
    return serverEnv.ZUS_WEBHOOK_SECRET;
  },
};

/**
 * DrChrono Configuration
 */
export const drchronoConfig = {
  get clientId() {
    return serverEnv.DRCHRONO_CLIENT_ID;
  },
  get clientSecret() {
    return serverEnv.DRCHRONO_CLIENT_SECRET;
  },
  get redirectUri() {
    return serverEnv.DRCHRONO_REDIRECT_URI;
  },
};

/**
 * DrChrono FHIR (ConnectEHR) Configuration
 * Used by lib/integrations/drchrono-fhir/ for FHIR R4 API access
 */
export const drchronoFhirConfig = {
  get baseUrl() {
    return process.env.DRCHRONO_FHIR_BASE_URL || "https://app.drchrono.com/api-docs/fhir";
  },
  get clientId() {
    return serverEnv.DRCHRONO_CLIENT_ID;
  },
  get clientSecret() {
    return serverEnv.DRCHRONO_CLIENT_SECRET;
  },
  get username() {
    return process.env.DRCHRONO_FHIR_USERNAME || undefined;
  },
  get password() {
    return process.env.DRCHRONO_FHIR_PASSWORD || undefined;
  },
  get tokenUrl() {
    return process.env.DRCHRONO_FHIR_TOKEN_URL || "https://drchrono.com/o/token/";
  },
  get isConfigured() {
    return Boolean(serverEnv.DRCHRONO_CLIENT_ID && serverEnv.DRCHRONO_CLIENT_SECRET);
  },
};
